import {money} from "../../utils/strings";

const LABEL_FONT = 'Graphik Trial, sans-serif';


export const formatLegendItem = (name, value, valuePercentChange, config, chartTheme) => {
  // Default configurations
  const color = config?.legend?.color ?? (chartTheme === 'dark' ? '#fff' : '#000');
  const prec = config?.legend?.precision || 2;
  const pre = config?.legend?.prefix || '';
  const post = config?.legend?.postfix || '';

  // Helper to format the value based on config
  const formatValue = (value, valuePercentChange, prec, format) => {
    if (format === '%') {
      const percentChangeColor = valuePercentChange >= 0 ? '#00995b' : '#bd1818';
      return `${value.toFixed(prec)} <span style="color: ${percentChangeColor}">: ${valuePercentChange?.toFixed(prec) ?? 0}%</span>`;
    } else if (format === '$') {
      return money(value); // Assuming money is some utility to format with currency
    }
    // Fallback: format using precision
    return value.toFixed(prec);
  };

  // Main formatter
  const getFormattedValue = (value, valuePercentChange, config) => {
    const format = config?.legend?.format;

    if (format) {
      return formatValue(value, valuePercentChange, prec, format);
    } else {
      // Default to rounding to 3 decimal places if no custom format is provided
      return value.toFixed(3);
    }
  };

  // Construct the final HTML
  const formattedValue = getFormattedValue(value, valuePercentChange, config);
  return `
    <div style="font-family:Graphik Trial, sans-serif;color:${color};text-transform:capitalize">
      <span style="color:rgba(0,0,0,0.8)">${name}</span>  
      <b style="color:${config?.color ?? color}">${formattedValue}</b>
    </div>
  `;
};


export const styleLegend = (chart, config, legendDirection='horizontal', chartTheme) => {
  // Assuming `config` includes properties like `color`.

  chart.legend().useHtml(true);
  chart.legend().positionMode('inside')
      .fontSize('12')
      .fontWeight('400')
      .maxWidth('100%')
      .itemsSpacing(legendDirection==='horizontal'?20:10)
      .itemsLayout(legendDirection)
      .fontFamily(LABEL_FONT);
  chart.legend().paginator().enabled(false)
 chart.legend().itemsFormatter((items) => {
    return items.map((item) => {
      item.iconType = 'triangle-left'; // Set icon type to 'circle'
      item.iconSize = 14
      item.iconFill = {
        ...item.iconFill,
        opacity: 0.2
      }
      item.iconStroke = {
        color: item.iconFill.color,
        opacity: 1,
        thickness: 2
      }
      return item;
    });
  });
  chart.legend().itemsFormat(function() {
    // `this` context refers to the legend item. Access the series name and value.
    try {
      return formatLegendItem(this?.seriesName, this?.value, this?.valuePercentChange, config, chartTheme);
    }
    catch (e) {
      return `${this?.seriesName}`
    }
  });
  chart.legend().titleFormat(function(){
    return ''
  })
};


export const styleScroller = (chart, scrollerDataMapping, config, chartTheme)=>{
  const scroller = chart.scroller();
  const bgColor = chartTheme === 'dark' ? '#1e1e1e' : '#F7F9FA';
  const thumbColor = chartTheme === 'dark' ? '#fff' : '#000';
  const overlayColor = chartTheme === 'dark' ? '#1a1a1a' : '#EFF3FF 0.5';
  scroller.fill(bgColor);
  scroller.selectedFill(overlayColor);
  scroller.thumbs().fill(thumbColor);
  scroller.xAxis().enabled(true);
  const column = scroller.column(scrollerDataMapping);
  column.stroke('transparent')
  column.fill({
        src: 'https://storage.googleapis.com/finiat-web-static-resources/chart-graphics/cold-color-pattern.jpeg',
        mode: 'stretch'
    });
  column.selected().fill({
        src: 'https://storage.googleapis.com/finiat-web-static-resources/chart-graphics/purplish-sunset-with-surfer.jpeg',
        mode: 'stretch'
    });

}

export const stylePlot = (plot, config, chartTheme) => {
  const dataArea = plot.dataArea();
  dataArea.background().enabled(true);
  const bgColor = chartTheme === 'dark' ? '#1e1e1e' : '#fff';
  const borderColor = chartTheme === 'dark' ? '#333' : '#ededed';
  const axisColor = chartTheme === 'dark' ? '#555' : '#e0e0e0';
  const fontWeight = chartTheme === 'dark' ? '600' : '400';
  // plot.background().fill(bgColor); // Set plot border color
  dataArea.background().fill(bgColor).corners(0);
  dataArea.background().bottomStroke(`2 ${axisColor}`)

  if (config.hasOwnProperty('height')){
    plot.height(config.height);
  }
  plot.yGrid().enabled(config?.yGrid ?? true).stroke(config?.yGridStroke??{
    solid: '1',
    color: borderColor
  });
  plot.yMinorGrid().enabled(config?.yMinorGrid ?? false)?.stroke(config?.yMinorGridStroke ?? '1 #f1f1f1');
  plot.xGrid().enabled(config?.xGrid ?? true).stroke({
    solid: '1',
    color: borderColor
  })
  plot.yAxis(0).stroke(`1 ${axisColor}`); // Left Y-axis
  plot.yAxis(0).ticks().stroke({ solid: '0', color: '#fff' });
  plot.yAxis(0).ticks().length(5);
  plot.yAxis(0).labels().format(`{%Value}${config?.yLabelPostfix??''}`)
      .fontColor('#000').fontFamily(LABEL_FONT).fontSize(10).fontWeight(fontWeight); // Format labels with %

  // Enable and style the right Y-axis
  plot.yAxis(1).enabled(true).orientation("right");
  plot.yAxis(1).stroke('transparent'); // Right Y-axis
  plot.yAxis(1).ticks().stroke({ solid: '2', color: 'transparent' });
  plot.yAxis(1).ticks().length(5);
  plot.yAxis(1).labels().format(`{%Value}${config?.yLabelPostfix??''}`)
      .fontWeight(fontWeight).fontColor('#000').fontFamily(LABEL_FONT).fontSize(10); // Format labels with %

  plot.xAxis().minorLabels().enabled(false);
  plot.xAxis().labels().fontColor('#000').fontFamily(LABEL_FONT).fontSize(12);
};






export const finishDrawingChart = (chart, xTicks, ref, title, xPadding=50, yPadding=10, chartTheme)=>{
  chart.tooltip().useHtml(true);
  chart.title(title);
  chart.margin([0, 0, 0, 0]);
  chart.padding([yPadding, xPadding, yPadding, xPadding]);
  chart.xScale().ticksCount(xTicks);
  // chart.background().fill(chartTheme === 'dark' ? '#1a1a1a' : '#FFF');
  chart.container(ref);
  chart.draw();
}